/**
 * Typed client for the caller's own active sessions (ADR-0146 §3.5, §3.6):
 *
 *   GET  /v1/account/sessions               — caller's sessions only
 *   POST /v1/account/sessions/revoke-others — bulk DELETE except current
 *
 * The list is scoped server-side to the caller's `user_id` (taken from
 * the resolved Principal, never a request param). Per-row revoke
 * (ADR-0148, Slice 2) — `DELETE /v1/account/sessions/:session_id` —
 * revokes one of the caller's own sessions by its public `session_id`;
 * the current session can't be revoked this way (409, sign-out lives
 * on the auth surface).
 *
 * `is_current` flips on exactly one row (matched by `session_id_hash`
 * server-side); the dashboard can use it to render the "this device"
 * pill without trusting client identity.
 */

import type { SessionWire, SessionsResponse } from '@sill/types/api';
import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export type GetSessionsResult =
  | { readonly kind: 'ok'; readonly sessions: readonly SessionWire[] }
  | { readonly kind: 'error'; readonly message: string };

export type RevokeOthersResult =
  | { readonly kind: 'ok'; readonly revoked: number }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type RevokeSessionResult =
  | { readonly kind: 'ok' }
  // The row was gone (already revoked elsewhere, or refreshed away).
  // Treated as success-ish by the UI: the session is no longer active.
  | { readonly kind: 'not_found' }
  // The caller targeted their own current session — not allowed here.
  | { readonly kind: 'current_session' }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

interface RevokeOthersWire {
  readonly revoked?: unknown;
}

export async function getSessions(
  http: HttpClient,
): Promise<GetSessionsResult> {
  try {
    const res = await http.request<SessionsResponse>({
      method: 'GET',
      path: '/v1/account/sessions',
    });
    if (res === undefined || !Array.isArray(res.sessions)) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', sessions: res.sessions };
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'error', message: explainApiError(err) };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

export async function revokeOtherSessions(
  http: HttpClient,
): Promise<RevokeOthersResult> {
  try {
    const res = await http.request<RevokeOthersWire>({
      method: 'POST',
      path: '/v1/account/sessions/revoke-others',
      body: {},
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const revoked =
      typeof res.revoked === 'number' && Number.isFinite(res.revoked)
        ? res.revoked
        : 0;
    return { kind: 'ok', revoked };
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'rate_limited',
        message:
          "You've signed out everywhere too many times. Wait a minute and try again.",
      };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

export async function revokeSession(
  http: HttpClient,
  session_id: string,
): Promise<RevokeSessionResult> {
  try {
    await http.request({
      method: 'DELETE',
      path: `/v1/account/sessions/${encodeURIComponent(session_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'rate_limited',
        message:
          "You've signed sessions out too many times. Wait a minute and try again.",
      };
    }
    if (err instanceof ApiError) {
      // 404 — the row is already gone (revoked elsewhere / list stale).
      if (err.kind === 'not_found') return { kind: 'not_found' };
      // 409 — the only conflict this route returns is "that's your
      // current session" (cannot_revoke_current_session).
      if (err.kind === 'conflict') return { kind: 'current_session' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Sessions service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
